/**
 *
 * A sub-sequence within an arrangement. Contains pattern events
 * that can be resized.
 * If the sub-sequence is "expanded", the underlying tracks will be
 * displayed underneath it.
 */

import React, { useRef, useEffect, useState, useCallback, Component } from 'react';
import SubSequenceTracks from './tracks/SubSequenceTracks.js';
import { connect } from "react-redux";
import { getIsSelected} from './selectors.js';
import {dragPattern} from 'actions/index.js';
import {dispatch} from 'session/operations/dispatch.js';
import {resizePianoRollPattern} from 'session/operations/actions.js';
import MouseHelper from './MouseHelper.js';
import {DEFAULT_RESOLUTION} from './constants.js';
import MiniPianoRoll from './MiniPianoRoll.js';
import RowSelection from './RowSelection.js';
import VolumeChangeAutomation from './automation/VolumeChangeAutomation.js';
import SubSequenceSidePanel from './SubSequenceSidePanel.js';
import {quantizeStep} from './quantizeStep.js';
import PatternComponent from './PatternComponent.js';
import {getStepFromPosition} from './getStepFromPosition.js';
import {useRowSelection} from './useRowSelection.js';
import {useScroll} from './useScroll.js';
import {getWindowedEvents} from './getWindowedEvents.js';
import {getBarWidth} from './getBarWidth.js';
import {insertPatternEventAtCursor} from './cursor.js';
import PatternSwitchEvent from 'session/PatternSwitchEvent.js';
import {
    setArrangementMouseX,
    setArrangementCursorStep,
    setArrangementPatternSelected,
    setSelectedArrangementEvents,
    setArrangementSelectedRows,
    setArrangementRowSelections,
} from 'actions/index.js';
import {
    PATTERN_SWITCH,
    SUB_SEQUENCE_VOLUME_CHANGE,
    SEQUENCE_ROLL,
    RESOLUTION_CHANGE} from 'session/constants.js';
import {LEFT, RIGHT} from './constants.js';

const CLICK_THRESHOLD = 200;

const SubArrangementComponent = (props) => {

    const getState = () => {
        return props.sequencerEngine.store.getState();
    };

    const patternSwitchEvents = () => {
        let mixEvents = getState().arrangement.mixEvents[props.subSequence.id];
        return mixEvents !== undefined ? mixEvents[PATTERN_SWITCH] || [] : [];
    };

    const {visibleView} = useScroll({
        ref: props.wrapperRef,
        resolution: props.resolution,
        xScale: props.xScale,
        events: patternSwitchEvents(),
        sequencerEngine: props.sequencerEngine,
        eventsLength: props.eventsLength
    });

    const {onRowSelectionUp, onRowSelectionDown, onRowSelectionMove} =
          useRowSelection({
              id: props.subSequence.id,
              sequencerEngine: props.sequencerEngine,
              xScale: props.xScale,
              resolution: props.resolution,
              setArrangementRowSelections: props.setArrangementRowSelections,
              scrollContainer: props.wrapperRef
          });

    const patternEventDragging = useRef(undefined);
    const resizing = useRef(false);
    const draggingDiff = useRef(0);
    const lastClickTime = useRef(undefined);
    const direction = useRef(undefined);

    const scrollLeft = () => props.wrapperRef.current.scrollLeft;

    const mix = props.subSequence.mix;

    const onDragPatternEvent = useCallback((e, patternEvent) => {
        e.stopPropagation();
        if (patternEvent.patternNumber === 0) {
            // we don't let you fuck with the first pattern
            return;
        }
        let step = getStepFromPosition(e.pageX + scrollLeft(), props.resolution, undefined, props.xScale);
        patternEventDragging.current = patternEvent;
        draggingDiff.current = step - patternEvent.step;
    }, [props.resolution, props.xScale]);

    // when the right edge of a pattern is dragged this
    // is called
    const onEdgeMouseDown = (event,  _direction) => {
        resizing.current = event;
        direction.current = _direction;
    };

    const onRowMouseUp = () => {
        let currentTime = new Date().getTime();
        if (lastClickTime.current &&
            currentTime - lastClickTime.current <= CLICK_THRESHOLD) {
            insertPatternEventAtCursor(mix, props.sequencerEngine.store);
        }
        lastClickTime.current = currentTime;
        onRowSelectionUp();
    };
     
    const onMouseUp = useCallback((event) => {
        resizing.current = undefined;
        direction.current = undefined;
        props.onMouseUp();
    }, [props.onMouseUp]);

    const onMouseMove = useCallback((e) => {
        let step = getStepFromPosition(e.pageX + scrollLeft(), props.resolution, undefined, props.xScale);
        let resolution = 32 / props.resolution.resolution;
        let remainder = step % resolution;
        step -= remainder;
        let resizedStep = Math.floor(step);

        if (resizing.current) {
           let difference = 
                step - (resizing.current.step + resizing.current.duration);

            if (direction.current === RIGHT) {
                let nextEvent = mix.getNextEvent(resizing.current);
                if (nextEvent) {
                    let nextStepAfter = mix.getNextEvent(nextEvent);
                    if (nextStepAfter) {
                        // don't allow resizing past the following event
                        if (resizedStep < nextStepAfter.step &&
                            resizing.current.step < resizedStep) {
                            nextEvent.step = resizedStep;
                            mix.updateAllTypeRedux();
                        }
                    } else if (resizing.current.step < resizedStep) {
                        nextEvent.step = resizedStep;
                        mix.updateAllTypeRedux();
                    }
                }
            } else {
                let previousEvent = mix.getPreviousEvent(resizing.current);
                let nextStep = mix.getNextEvent(resizing.current);
                if (previousEvent === undefined || resizedStep > previousEvent.step) {
                    if (nextStep === undefined || resizedStep < nextStep.step) {
                        resizing.current.step = resizedStep;
                        mix.updateAllTypeRedux();
                    }
                } 
            }
            if (!props.isSelected) {
                selectRow();
            }
        } else if (patternResizing()) {
            let {pattern, direction} = patternResizing();
            dispatch(resizePianoRollPattern(pattern, direction, resizedStep));
        } else {
            onRowSelectionMove(e, props.resolution, props.xScale);
        }
    }, [props.resolution, props.xScale, props.isSelected]);

    const onMouseLeave = () => {
        resizing.current = undefined;
    };

    const onDrop = useCallback((event) => {
        let step = getStepFromPosition(
            event.pageX + scrollLeft(),
            props.resolution,
            undefined,
            props.xScale);
        step = quantizeStep(step, props.resolution);

        if (props.patternDragging !== undefined) {
            let patternSwitch = new PatternSwitchEvent(
                props.patternDragging);

            let resolution = 32 / props.resolution.resolution;
            let remainder = step % resolution;
            step -= remainder;
            patternSwitch.step = step;
            mix.insertEvent(
                patternSwitch, 32);
            props.dragPattern(undefined);
        } else if (patternEventDragging.current !== undefined) {
            let event = patternEventDragging.current;
            mix.deleteEvent(event);
            step -= draggingDiff.current;
            let resolution = 32 / props.resolution.resolution;
            let remainder = step % resolution;
            step -= remainder;
            event.step = step;
            mix.insertEvent(
                event, event.duration);

            patternEventDragging.current = undefined;
        }
    }, [props.resolution, props.patternDragging]);

    const onSelect = (event) => {
        console.log('on select');
        let _selectedEvents = {... selectedEvents()} || {};

        props.setArrangementCursorStep(event.step);
        props.setArrangementMouseX(undefined);
        if (isSelected(event)) {
            delete _selectedEvents[event.id];

            props.setSelectedEvents(selectedEvents);
            props.setArrangementRowSelections({});
            return;
        }

        props.setArrangementRowSelections({});
        if (getState().arrangement.patternSelected) {
            props.setPatternSelected(event);
        }
        _selectedEvents = {};
        _selectedEvents[event.id] = event;
        props.setSelectedEvents(_selectedEvents);
    };

    const isSelected = (event) => {
        return selectedEvents() === undefined ?
            false :
            selectedEvents()[event.id] !== undefined;
    };

    const onDragOver = (e) => {
        e.preventDefault();
    };


    const selectedEvents = () => {
        return getState().arrangement.selectedEvents;
    };

    const patternResizing = () => {
        return getState().arrangement.patternResizing;
    };

    const selectRow = () => {
        props.setSelectedRows([props.subSequence.id]);
        props.sequencerEngine.switchToSubSequence(props.subSequence.id, false);
    };

    const onMouseDown = useCallback((e) => {
        onRowSelectionDown(e);
        selectRow();
    }, [props.resolution, props.xScale]);

    let eventComponents = '';
    if (props.subSequence.isTrack) {
        eventComponents = (
            <MiniPianoRoll
              track={props.subSequence.tracks[0]}
              xScale={props.xScale}
              resolution={props.resolution}
              sequencerEngine={props.sequencerEngine}
              subSequence={props.subSequence}/>
        );
    } else {
        let _patternSwitchEvents = patternSwitchEvents() || [];
        let smallPattern = false;
        if (props.automationTypeToShow === SUB_SEQUENCE_VOLUME_CHANGE) {
            smallPattern = true;
        }
        let eventsToShow = [];
        if (visibleView.start !== undefined) {
            eventsToShow = 
                _patternSwitchEvents.slice(visibleView.start, visibleView.end + 1);
        }
        eventComponents = [
            (<div className="pattern-filler" style={{width: visibleView.preWidth}}/>),
            ...eventsToShow .map(
                    (event, i) =>
                        <PatternComponent
                          i={i}
                          smallPattern={smallPattern}
                          key={event.id}
                          type={PATTERN_SWITCH}
                          resolution={props.resolution}
                          subSequence={props.subSequence}
                          isLast={i === patternSwitchEvents.length - 1}
                          isFirst={i === 0}
                          onDragStart={(e) => onDragPatternEvent(e, event)}
                          onSelect={() => onSelect(event)}
                          onRightMouseDown={(e) => onEdgeMouseDown(event, RIGHT)}
                          onMouseUp={onMouseUp}
                          xScale={props.xScale}
                          onMouseLeave={onMouseLeave}
                          onLeftMouseDown={(e) => onEdgeMouseDown(event, LEFT)}
                          sequencerEngine={props.sequencerEngine}
                          event={event}
                          arrangementContainer={props.wrapperRef}
                        />)
        ];
    }

    let automations = '';
    if (props.automationTypeToShow !== undefined &&
        props.automationTypeToShow !== PATTERN_SWITCH) {
        automations = (
            <VolumeChangeAutomation
              eventType={props.automationTypeToShow}
              sequencerEngine={props.sequencerEngine}
              subSequence={props.subSequence}/>
        );
    }

    let className = "pattern-row";
    if (props.isSelected) {
        className += ' selected';
    }
    let smallPattern = false;
    if (props.automationTypeToShow === SUB_SEQUENCE_VOLUME_CHANGE) {
        smallPattern = true;
        className += ' automation';
    }

    let style = {
        width: props.width
    };

    className += " " + props.subSequence.id;
    return (
        <div className="sub-sequence-arrangement-container">
          <div
            style={style}
            onClick={selectRow}
            onDrop={onDrop}
            onDragOver={onDragOver}
            onMouseDown={onMouseDown}
            onMouseUp={onRowMouseUp}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            className={className}>
            <RowSelection id={props.subSequence.id}/>
            <MouseHelper subSequence={props.subSequence}/>
            <div
              className="patterns-arrangement-container">
              {eventComponents}
            </div>
            {automations}
          </div>
          <SubSequenceTracks
            width={props.width}
            yScrollContainer={props.yScrollContainer}
            wrapperRef={props.wrapperRef}
            style={style}
            subSequence={props.subSequence}/>
        </div>
    );
}

const mapDispatchToProps = (dispatch, props) => {
    let subSequenceId = props.subSequence.id;
    return {
        dragPattern : p => dispatch(dragPattern(p)),
        setSelectedRows: p => dispatch(setArrangementSelectedRows(p)),
        setPatternSelected: p => dispatch(setArrangementPatternSelected(p)),
        setSelectedEvents: (events) => dispatch(
            setSelectedArrangementEvents(events)),
        setArrangementRowSelections: (selections) => dispatch(
            setArrangementRowSelections(selections)),
        setArrangementMouseX: (x) => dispatch(
            setArrangementMouseX(x)),
        setArrangementCursorStep: (x) => dispatch(
            setArrangementCursorStep(x))
    };
};
 
const mapStateToProps = (state, props) => {
    let mixEvents = state.arrangement.mixEvents[props.subSequence.id];
    let patternSwitchEvents = mixEvents !== undefined ?
        mixEvents[PATTERN_SWITCH] : undefined;
    return {
        eventsLength: patternSwitchEvents === undefined ? 0 : patternSwitchEvents.length,
        patternDragging: state.sequencer.patternDragging,
        resolution: DEFAULT_RESOLUTION,
        automationTypeToShow: state.arrangement.automationTypeToShow[props.subSequence.id],
        isSelected: getIsSelected(state, props),
        xScale: state.arrangement.xScale,
    };
};

export default connect(mapStateToProps, mapDispatchToProps)(SubArrangementComponent);
